<template>
    <div class="classics-timetracker">
        <div v-if="loading">
            <p>Loading</p>
            <button type="button" class="btn btn-danger" v-if="loadingNeedsRescue" v-on:click="reset">Having trouble? Click here to reset</button>
        </div>
        <div v-else-if="state == 'allStaff'" class="row">
            <div v-for="staff in this.staff" class="col-3 col-md-6 col-lg-4">
                <div class="block block-rounded text-center" v-on:click="getTimeTrackerStatusFor(staff.uuid)">
                    <div class="block-header">
                        <h3 class="block-title">
                            <span v-if="staff.display_name">
                                {{ staff.display_name }}
                            </span>
                            <span v-else>
                                {{ staff.name }}
                            </span>
                        </h3>
                    </div>
                    <div class="block-content bg-body-light" v-if="staff.current_time_entry">
                        <p class="h4 text-muted">Currently working on</p>
                        <p class="h2" v-if="staff.current_time_entry.job">
                            {{ staff.current_time_entry.job.vehicle.description }}<br />
                            {{ staff.current_time_entry.job.description }}
                        </p>
                        <p class="h2" v-else>
                            {{ staff.current_time_entry.description }}
                        </p>
                        <p class="h6"><time-since :since="staff.current_time_entry.since" /></p>
                    </div>
                    <div class="block-content block-content-full" :class="{'bg-body-light': !staff.current_time_entry}">
                        <span class="btn btn-primary px-4">Select</span>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="state == 'jobs'">
            <h2>
                Jobs Assigned To
                <span v-if="staff.display_name">
                    {{ staff.display_name }}
                </span>
                <span v-else>
                    {{ staff.name }}
                </span>
            </h2>
            <div class="row">
                <div v-for="job in this.jobs" class="col-lg-6 col-12">
                    <div class="block block-rounded">
                        <div class="block-content block-content-full ribbon">
                            <div class="ribbon-box">
                                <button class="btn btn-primary" :disabled="cannotStart" v-on:click="startJob(job.uuid)">
                                    <i class="far fa-clock"></i> Start Job
                                </button>
                            </div>
                            <div class="fs-sm fw-semibold text-uppercase text-muted">{{ job.vehicle.description }}</div>
                            <div class="fs-2 fw-normal text-dark">{{ job.description }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6 col-12">
                    <div class="block block-rounded">
                        <div class="block-content block-content-full ribbon">
                            <div class="ribbon-box">
                                <button class="btn btn-primary" :disabled="cannotStart" v-on:click="startJob()">
                                    <i class="far fa-clock"></i> Start Job
                                </button>
                            </div>
                            <input class="form-control fs-2 fw-normal text-dark mt-2" v-model="newJobDescription" placeholder="TBC" style="width:80%">
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="state == 'tracking'">
            <h2>{{ tracking.staff }}</h2>
            <div class="row">
                <div class="col-lg-4 col-md-4 col-sm-12">
                    <div class="block block-rounded">
                        <div class="block-header block-header-default">
                            <h3 class="block-title">Vehicle</h3>
                        </div>
                        <div class="block-content">
                            <p v-if="tracking.job">{{ tracking.job.vehicle.description }}</p>
                            <p v-else class="text-muted">n/a</p>
                        </div>
                    </div>
                    <div class="block block-rounded">
                        <div class="block-header block-header-default">
                            <h3 class="block-title">Job</h3>
                        </div>
                        <div class="block-content">
                            <p v-if="tracking.job">{{ tracking.job.description }}</p>
                            <p v-else>{{ tracking.description }}</p>
                        </div>
                    </div>
                    <div class="block block-rounded">
                        <div class="block-content">
                            <p>Currently tracked <time-since :since="tracking.since" /></p>

                            <button class="btn btn-primary mb-4" v-on:click="finishJob(tracking.entry, tracking.job)">
                                Stop tracking for now
                            </button>
                        </div>
                    </div>

                    <div v-if="tracking.job" class="block block-rounded">
                        <div class="block-header block-header-default">
                            <h3 class="block-title">Add Photos</h3>
                        </div>
                        <div class="block-content">
                            <div class="mb-4">
                                <input class="form-control" type="file" id="addPhotoToVehicle" accept="image/*" capture="camera" ref="file-upload-input" v-on:change="processFiles">
                            </div>

                            <table v-if="files.length" class="table mb-4" style="table-layout:fixed">
                                <thead>
                                    <tr>
                                        <th style="width:50%">Name</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(file, index) in files" :key="index">
                                        <td v-if="file.uuid === false">
                                            <span style="text-overflow:ellipsis;overflow:hidden;display:block;white-space:nowrap;">
                                                {{ file.rawFile.name }}
                                            </span>
                                        </td>
                                        <td v-if="file.uuid === false">
                                            <p v-if="file.errors.length">
                                                {{ file.errors.join(', ') }}
                                            </p>
                                            <div v-else class="progress push" style="height:10px;width:100%;">
                                                <div class="progress-bar progress-bar-striped progress-bar-animated bg-success" role="progressbar" :style="{ width: file.uploadPercentage + '%'}" :aria-valuenow="file.uploadPercentage" aria-valuemin="0" aria-valuemax="100"></div>
                                            </div>
                                        </td>
                                        <td v-if="file.uuid !== false" colspan="2">
                                            <div class="input-group">
                                                <input type="text" class="form-control" v-model="file.title" :placeholder="file.rawFile.name" v-on:change="updateTitle(index)">
                                                <span v-if="file.loading !== null" class="input-group-text">
                                                    <div v-if="file.loading === true" class="spinner-border spinner-border-sm text-primary" role="status">
                                                        <span class="visually-hidden">Loading...</span>
                                                    </div>
                                                    <i v-else class="fa fa-check"></i>
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="col-lg-8 col-md-8 col-sm-12">
                    <div class="block block-rounded">
                        <div class="block-header block-header-default">
                            <h3 class="block-title">Add Note</h3>
                        </div>
                        <div class="block-content">
                            <div class="mb-4">
                                <input class="form-control" type="text" v-model="newNoteContent">
                                <button class="btn btn-primary mt-4" v-on:click="createNewNote">Add</button>
                            </div>
                        </div>
                    </div>
                    <div class="block block-rounded">
                        <div class="block-header block-header-default">
                            <h3 class="block-title">Old Notes</h3>
                        </div>
                        <div class="block-content">
                            <table class="table mb-4" style="table-layout:fixed" ref="notes-datatable">
                                <thead>
                                    <tr>
                                        <th>Author</th>
                                        <th>Date</th>
                                        <th>Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

        </div>
        <button class="btn btn-secondary" v-if="!loading && state != 'allStaff'" v-on:click="reset">
            Back to everyone
        </button>
    </div>
</template>

<script>
import axios from 'axios';
import timeSince from './../timesince.vue';

const baseURL = 'timetracker';

export default {
    components: {
        timeSince,
    },
    props: {
        routes: Object
    },
    data () {
        return {
            cannotStart: false,
            currentStaffUuid: null,
            files: [],
            loading: false,
            loadingNeedsRescue: false,
            loadingRescueTimeout: null,
            newJobDescription: '',
            state: '',
            staff: [],
            jobs: [],
            tracking: [],
            notesDatatable: null,
            newNoteContent: '',
        };
    },
    mounted () {
        this.reset();

        window.onpopstate = () => {
            // when the back button is pressed, if the URL is now the base then reset
            if (window.location.pathname == '/' + baseURL) {
                this.reset();
                return;
            }

            let path = window.location.pathname.split('/');
            this.getTimeTrackerStatusFor(path[2]);
        };
    },
    methods: {
        initialiseNotesDatatable() {
            this.notesDatatable = window.jQuery(this.$refs['notes-datatable']).DataTable({
                dom: 'frtip', // remove 'l' to hide page changer
                pageLength: 5,
                processing: true,
                serverSide: true,
                ajax: {
                    url: this.routes.getNotes.replace('replace-me', this.tracking.entry.uuid),
                },
                order: [[1, 'desc']],
                columns: [
                    {
                        data: 'author.name',
                        width: '30%',
                        orderable: false,
                    },
                    {
                        data: 'created_at',
                        width: '20%',
                    },
                    {
                        data: 'content',
                    },
                ]
            });
        },
        doNotCloseWindow (ev) {
            ev.preventDefault();
            return ev.returnValue = 'Are you sure you want to exit?';
        },
        getTimeTrackerStatusFor (staffUuid) {
            this.setLoading(true);
            this.currentStaffUuid = staffUuid;
            axios.get(this.routes.timeTrackingStatus.replace('replace-me', staffUuid))
                .then((response) => {
                    if (response.data.currently == 'not_on_a_job') {
                        this.listJobsFor(staffUuid);
                    } else {
                        this.tracking = response.data;
                        this.setState('tracking', [staffUuid, (response.data.job !== null) ? response.data.job.uuid : null]);
                        this.setLoading(false);
                        setTimeout(this.initialiseNotesDatatable, 50);
                    }
                });
        },
        listJobsFor (staffUuid) {
            this.setLoading(true);
            this.currentStaffUuid = staffUuid;
            axios.get(this.routes.jobsList + '/' + staffUuid)
                .then((response) => {
                    this.jobs = response.data.jobs;
                    this.staff = response.data.staff;
                    this.setState('jobs', [staffUuid]);
                    this.setLoading(false);
                });
        },
        processFiles () {
            if (this.state != 'tracking') {
                alert('how did you get here?');
                return;
            }

            for (let file of this.$refs['file-upload-input'].files) {
                this.files.push({
                    uuid: false,
                    title: '',
                    errors: [], // this is out of use now but could be neat in the future
                    loading: null,
                    rawFile: file,
                    uploadPercentage: 0,
                });
                this.processFile(this.files.length - 1);
            }
            // clear the input so its not confusing
            this.$refs['file-upload-input'].value = '';
        },
        async processFile (index) {
            let file = this.files[index].rawFile;

            let uploadDetails;

            try {
                uploadDetails = await axios.post(
                    this.routes.getUploadUrl.replace('replace-me', this.tracking.job.vehicle.uuid),
                    {
                        name: file.name,
                        size: file.size,
                    }
                ).then(({ data }) => data);
            } catch (error) {
                let status = 'Unknown';
                if (error.response) {
                    if (error.response.data && error.response.data.errors) {
                        status = error.response.data.errors[0].join(', ');
                    } else {
                        status = error.response.status;
                    }
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Could not upload file ' + file.name,
                    text: 'System error: ' + status,
                });

                // abort upload
                return;
            }

            let data = new FormData();
            for (let key in uploadDetails.inputs) {
                data.append(key, uploadDetails.inputs[key]);
            }
            data.append('key', uploadDetails.destination)
            data.append('file', file);

            let success = false;

            await axios.post(
                uploadDetails.action,
                data,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        this.files[index].uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                    }
                }
            ).then(() => {
                success = true;
            }).catch((error) => {
                let status = 'Unknown';
                if (error.response) {
                    status = error.response.status;
                }
                Swal.fire({
                    icon: 'error',
                    title: 'Could not upload file ' + file.name,
                    text: 'System error: ' + status,
                });
            });

            if (success) {
                axios.post(
                    this.routes.completeUpload.replace('replace-me', this.tracking.job.vehicle.uuid),
                    {
                        'internalSignature': uploadDetails.internalSignature
                    }
                ).then((response) => {
                    this.files[index].uuid = response.data.fileUuid;
                }).catch((error) => {
                    let status = 'Unknown';
                    if (error.response) {
                        status = error.response.status;
                    }
                    Swal.fire({
                        icon: 'error',
                        title: 'Could not upload file',
                        text: 'System error: ' + status,
                    });
                });
            }
        },
        reset () {
            this.currentStaffUuid = null;
            this.setState('allStaff', []);
            this.setLoading(true);
            axios.get(this.routes.staffList)
                .then((response) => {
                    this.staff = response.data.staff;
                    this.setLoading(false);
                });
        },
        setLoading (isLoading) {
            this.loadingNeedsRescue = false;
            this.loading = isLoading;
            if (isLoading) {
                this.loadingRescueTimeout = setTimeout(() => {
                    this.loadingNeedsRescue = true;
                }, 5000);
            } else {
                clearTimeout(this.loadingRescueTimeout);
            }
        },
        setState (state, href) {
            // if we are changing from jobs to something else then
            // ensure the TBC field is emptied
            if (this.state == 'jobs') {
                this.newJobDescription = '';
            }

            this.state = state;

            href.unshift(baseURL);

            href = '/' + href.join('/');

            // the second href changes the URL in the browser
            window.history.pushState(href, null, href);

            // in case the popstate does not catch you, try and avoid closing the window accidentally
            if (state == 'allStaff') {
                window.addEventListener('beforeunload', this.doNotCloseWindow, {capture: true});
            } else {
                window.removeEventListener('beforeunload', this.doNotCloseWindow, {capture: true});
            }
        },
        startJob (jobUuid) {
            this.cannotStart = true;

            let route = (jobUuid !== undefined)
                ? this.routes.startTrackingJob.replace('replace-me', jobUuid)
                : this.routes.startNewJob;

            axios.post(route, {
                newJobDescription: this.newJobDescription,
                staffUuid: this.currentStaffUuid,
            }).then((response) => {
                if (response.data.success === true) {
                    this.newJobDescription = '';
                    Swal.fire({
                        icon: 'success',
                        title: 'Started job',
                        timer: 1500,
                        timerProgressBar: true,
                        showConfirmButton: false,
                    });
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Could not start job',
                        text: 'Are you already tracking time?',
                    });
                }
                this.reset();
            }).catch((error) => {
                let status = 'Unknown';
                if (error.response) {
                    status = error.response.status;
                }
                Swal.fire({
                    icon: 'error',
                    title: 'Could not start job',
                    text: 'System error: ' + status,
                });
            }).finally(() => {
                this.cannotStart = false;
            });
        },
        async finishJob (entry, job) {
            const jobIsDonePrompt = await Swal.fire({
                title: 'Is the job completed?',
                showDenyButton: true,
                showCancelButton: false,
                confirmButtonText: 'Yes',
                denyButtonText: `No`,
            })
            let route = this.routes.endTrackingJob
                .replace('replace-me-entry', entry.uuid)
                .replace('/replace-me-job', (job !== null) ? '/' + job.uuid : '');

            axios.post(route, {
                staffUuid: this.currentStaffUuid,
                jobIsDone: jobIsDonePrompt.isConfirmed
            }).then((response) => {
                if (response.data.success === true) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Stopped tracking job',
                        timer: 1500,
                        timerProgressBar: true,
                        showConfirmButton: false,
                    });
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Could not stop tracking job',
                        text: 'Are you already tracking time?',
                    });
                }
                this.reset();
            }).catch((error) => {
                let status = 'Unknown';
                if (error.response) {
                    status = error.response.status;
                }
                Swal.fire({
                    icon: 'error',
                    title: 'Could not stop tracking job',
                    text: 'System error: ' + status,
                });
            });
        },
        updateTitle (index) {
            this.files[index].loading = true;
            axios.post(
                this.routes.updateTitle.replace('replace-me', this.files[index].uuid),
                {
                    title: this.files[index].title
                }
            ).then((response) => {
                this.files[index].loading = false;
            }).catch((error) => {
                let status = 'Unknown';
                if (error.response) {
                    status = error.response.status;
                }
                Swal.fire({
                    icon: 'error',
                    title: 'Could not update title',
                    text: 'System error: ' + status,
                });
            });
        },

        createNewNote() {
            if (this.newNoteContent.trim().length == 0) {
                return;
            }

            axios.post(this.routes.addNewNote, {
                note: this.newNoteContent,
                staffUuid: this.currentStaffUuid,
                jobUuid: this.tracking.job?.uuid,
                entryUuid: this.tracking.entry.uuid
            }).then(() => {
                Swal.fire('Successfully saved the note');
                this.newNoteContent = '';
                this.notesDatatable.ajax.reload();
            }).catch((e) => {
                if (e.status > 499) {
                    Swal.fire('An internal problem occurred.');
                } else if (e.status > 399) {
                    Swal.fire('We could not save the note. Please check your input and try again.');
                }
            });
        }
    }
}
</script>
